import { ReactComponent as ConfettiSuccessIcon } from '@assets/icons/confetti-success.svg';
import { Snackbar } from '@mui/material';
import { CheckCircle, CircleAlert, CircleX, Info, X } from 'lucide-react';
import React from 'react';
import * as S from './styles';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

export type ToastSize = 'small' | 'big';

export interface ToastConfig {
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  type?: ToastType;
  size?: ToastSize;
  title?: string;
  message: React.ReactNode;
  duration?: number;
}

export interface ToastProps {
  open: boolean;
  onClose: (event?: React.SyntheticEvent | Event, reason?: string) => void;
  type: ToastType;
  size?: ToastSize;
  title?: string;
  message: React.ReactNode;
  duration?: number;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

const ICONS = {
  success: CheckCircle,
  error: CircleX,
  warning: CircleAlert,
  info: Info,
};

const Toast: React.FC<ToastProps> = ({
  open,
  onClose,
  type = 'info',
  size = 'small',
  title,
  message,
  duration = 6000,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
}) => {
  const Icon = ICONS[type];

  return (
    <Snackbar open={open} autoHideDuration={duration} onClose={onClose} anchorOrigin={anchorOrigin}>
      <S.ToastWrapper type={type} size={size}>
        <S.IconWrapper type={type} isConfetti={size === 'big' && type === 'success'}>
          {size === 'big' && type === 'success' ? (
            <ConfettiSuccessIcon />
          ) : (
            <Icon size={18} strokeWidth={2.5} />
          )}
        </S.IconWrapper>
        <S.Content size={size}>
          {/* If small, prefer title if available, otherwise message. If big, show both if available */}
          {size === 'small' ? (
            // For small, we treat "title" as the main text if present, or "message" if no title.
            // The user said "icon and title only", but usually users pass a message.
            // Let's display Title OR Message in the Title style.
            <S.Title type={type}>{title || message}</S.Title>
          ) : (
            <>
              {title && <S.Title type={type}>{title}</S.Title>}
              <S.Message type={type}>{message}</S.Message>
            </>
          )}
          {size === 'big' && (
            <S.CloseButton onClick={(e) => onClose(e, 'closeButtonClick')}>
              <X size={18} strokeWidth={2.5} />
            </S.CloseButton>
          )}
        </S.Content>
      </S.ToastWrapper>
    </Snackbar>
  );
};

export default Toast;
